import { BarChart, Bar, XAxis, YAxis, Tooltip, Text, ResponsiveContainer } from 'recharts';
import Spinner from 'react-spinkit';
import {
  getSchoolChartData,
  parseSchoolChartData,
  getSchoolChartFill,
  SchoolChartDataCalculationParams,
} from '../reportUtilities';
import styles from '../DistrictReport/DistrictChart.module.scss';
import { ReportingMixpanelEvent } from '../../../peekapak-types/ReportDataExchangeTypes';
import { useEffect, useRef, useState } from 'react';
import { logDebugLevel } from '../../GlobalFunctions';

interface Props {
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  legacyPlatformEvents: ReportingMixpanelEvent[];
  newMixpanelEvents: ReportingMixpanelEvent[];
  eventToChart: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  isLoading: boolean;
  loadingStatus: string;
}

const MIN_BAR_WIDTH = 20;
const GAP_WIDTH = 5;

const SchoolChart = ({
  legacyPeekavilleEvents,
  legacyPlatformEvents,
  newMixpanelEvents,
  eventToChart,
  startDate,
  endDate,
  isLoading,
  loadingStatus,
}: Props): JSX.Element => {
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [parsedChartData, setParsedChartData] = useState(undefined);
  const [barKeys, setBarKeys] = useState(undefined);
  const [isParametersSet, setIsParametersSet] = useState(false);

  // ResizeObserver to monitor container width changes
  useEffect(() => {
    const updateWidth = () => {
      if (chartContainerRef.current) {
        setContainerWidth(chartContainerRef.current.getBoundingClientRect().width);
      }
    };

    // Update width on mount
    updateWidth();

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        logDebugLevel(`SchoolChart: ResizeObserver update width: ${entry.contentRect.width}`);
        setContainerWidth(entry.contentRect.width);
      }
    });

    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      if (chartContainerRef.current) {
        resizeObserver.unobserve(chartContainerRef.current);
        logDebugLevel('SchoolChart: ResizeObserver undoing');
      }
    };
  }, []);

  // Calculate optimal number of bars based on container width and bar width constraints
  useEffect(() => {
    if (containerWidth > 0 && startDate && endDate) {
      const params: SchoolChartDataCalculationParams = {
        startDate,
        endDate,
        minBarWidth: MIN_BAR_WIDTH,
        gapWidth: GAP_WIDTH,
        containerWidth,
      };

      const data = getSchoolChartData(legacyPeekavilleEvents, legacyPlatformEvents, newMixpanelEvents, params);
      const parsedChartData = data && parseSchoolChartData(data, eventToChart);
      setParsedChartData(parsedChartData);
      const barKeys = parsedChartData && Object.keys(parsedChartData[0]).slice(1);
      setBarKeys(barKeys);
      setIsParametersSet(true);
      logDebugLevel(`SchoolChart: Got data for width: ${containerWidth}`);
    } else {
      setIsParametersSet(false);
    }
  }, [
    containerWidth,
    eventToChart,
    newMixpanelEvents,
    legacyPeekavilleEvents,
    legacyPlatformEvents,
    startDate,
    endDate,
  ]);

  logDebugLevel('isParametersSet: ', isParametersSet);
  logDebugLevel('parsedChartData: ', parsedChartData);
  return (
    <div ref={chartContainerRef} className={styles.schoolContainer}>
      {isParametersSet && (
        <ResponsiveContainer width='100%' height={500}>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <div>
                <p>{loadingStatus}</p>
              </div>
              <Spinner />
            </div>
          ) : (
            <BarChart width={900} height={300} data={parsedChartData}>
              <XAxis dataKey='name' interval={0} tick={<CustomXAxisTick />} height={45} />
              <YAxis />
              <Tooltip />
              {barKeys &&
                barKeys.map((key, index) => (
                  <Bar dataKey={key} key={key} stackId='a' fill={getSchoolChartFill(index)} barSize={70}></Bar>
                ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      )}
      <h3 className={styles.chartLabel}>Period beginning:</h3>
    </div>
  );
};

interface TickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
}
const CustomXAxisTick = ({ x, y, payload }: TickProps) => {
  if (payload && payload.value) {
    return (
      <Text fontSize={'14px'} width={75} x={x} y={y} textAnchor='middle' verticalAnchor='start'>
        {payload.value}
      </Text>
    );
  }
  return null;
};

export default SchoolChart;
